import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { InstitutionProvider } from "./contexts/InstitutionDataContext";
import { motion, AnimatePresence } from 'framer-motion';
import FloatingBackButton from './components/FloatingBackButton';
import Modal from './components/Modal';
import LoadingOverlay from './components/LoadingOverlay';
import ThemeHandler from './components/ThemeHandler';
import GlobalBackground from './components/GlobalBackground';

// Componente para forzar el scroll al inicio en CADA cambio de ruta
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // 1. Scroll del navegador genérico (para móviles o si cambia Layout)
    window.scrollTo(0, 0);

    // 2. Scroll específico del contenedor principal en Desktop
    const dashboardContent = document.querySelector('.dashboard-content');
    if (dashboardContent) {
      dashboardContent.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
}

// Helper para lazy loading con reintentos automáticos
const lazyWithRetry = (componentImport) =>
  lazy(() =>
    componentImport()
      .then((component) => {
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
        return component;
      })
      .catch((error) => {
        const pageHasAlreadyBeenForceRefreshed = JSON.parse(
          window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
        );
        if (!pageHasAlreadyBeenForceRefreshed) {
          window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
          window.location.reload();
          return new Promise(() => { });
        }
        throw error;
      })
  );

// Lazy loading de páginas
const Login = lazyWithRetry(() => import('./pages/Login'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const InstitutionalClasses = lazyWithRetry(() => import('./pages/InstitutionalClasses'));
const InstitutionalClassForm = lazyWithRetry(() => import('./pages/InstitutionalClassForm'));
const InstitutionalClassDetail = lazyWithRetry(() => import('./pages/InstitutionalClassDetail'));
const StudentDetail = lazyWithRetry(() => import('./pages/StudentDetail'));
const AnecdotarioForm = lazyWithRetry(() => import('./pages/AnecdotarioForm'));
const AttendanceHistory = lazyWithRetry(() => import('./pages/AttendanceHistory'));
const AttendanceForm = lazyWithRetry(() => import('./pages/AttendanceForm'));
const AttendanceReport = lazyWithRetry(() => import('./pages/AttendanceReport'));
const ClassNotes = lazyWithRetry(() => import('./pages/ClassNotes'));
const AttendanceReportPending = lazyWithRetry(() => import('./pages/AttendanceReportPending'));
const PersonalClasses = lazyWithRetry(() => import('./pages/PersonalClasses'));
const PersonalClassDetail = lazyWithRetry(() => import('./pages/PersonalClassDetail'));
const PersonalClassForm = lazyWithRetry(() => import('./pages/PersonalClassForm'));
const Institution = lazyWithRetry(() => import('./pages/Institution'));
const Timetable = lazyWithRetry(() => import('./pages/Timetable'));
const GestionDocentes = lazyWithRetry(() => import('./pages/GestionDocentes'));
const StudentAttendanceHistory = lazyWithRetry(() => import('./pages/StudentAttendanceHistory'));
const AdminData = lazyWithRetry(() => import('./pages/AdminData'));
const PerfilDocente = lazyWithRetry(() => import('./pages/PerfilDocente'));
const InstitutionalReports = lazyWithRetry(() => import('./pages/InstitutionalReports'));
const ResumenGeneralSecciones = lazyWithRetry(() => import('./pages/ResumenGeneralSecciones'));
const TimeSlotAdmin = lazyWithRetry(() => import('./pages/TimeSlotAdmin'));
const Inicio = lazyWithRetry(() => import('./pages/Inicio'));
const StudentInstitutionalSearch = lazyWithRetry(() => import('./pages/StudentInstitutionalSearch'));
const Audit = lazyWithRetry(() => import('./pages/Audit'));
const AttendanceMasterCorrection = lazyWithRetry(() => import('./pages/AttendanceMasterCorrection'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const Calendario = lazyWithRetry(() => import('./pages/Calendario'));
const CalendarAdminPage = lazyWithRetry(() => import('./pages/CalendarAdminPage'));
const HelpModule = lazyWithRetry(() => import('./pages/HelpModule'));

const PageLoader = () => (
  <div style={{ backgroundColor: 'var(--color-bg)', minHeight: '100vh', position: 'relative' }}>
    <LoadingOverlay visible={true} />
  </div>
);

function PrivateRoute({ children }) {
  const { user, loading, isLinking } = useAuth();
  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--color-bg)', minHeight: '100vh', position: 'relative' }}>
        <LoadingOverlay visible={true} />
      </div>
    );
  }
  return (user && !isLinking) ? children : <Navigate to="/login" replace />;
}

export default function App() {
  const [updateModal, setUpdateModal] = React.useState({ open: false, registration: null });
  const location = useLocation();

  React.useEffect(() => {
    const handleUpdate = (e) => {
      setUpdateModal({ open: true, registration: e.detail });
    };
    window.addEventListener('pwa-update-available', handleUpdate);
    return () => window.removeEventListener('pwa-update-available', handleUpdate);
  }, []);

  const handleUpdateApp = () => {
    setUpdateModal({ open: false, registration: null });
    const registration = updateModal.registration;
    if (registration && registration.waiting) {
      sessionStorage.setItem('pwa_updating', 'true');
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
      setTimeout(() => {
        if (sessionStorage.getItem('pwa_updating')) {
          window.location.reload();
        }
      }, 2000);
    } else {
      window.location.reload();
    }
  };

  // Clave que solo cambia entre Login y el App principal para evitar re-animar todo en sub-rutas aquí
  const authAreaKey = location.pathname.startsWith('/login') ? 'auth' : 'app';

  return (
    <InstitutionProvider>
      <ThemeHandler />
      <GlobalBackground />
      <ScrollToTop />
      <FloatingBackButton />

      <AnimatePresence mode="wait">
        <motion.div
          key={authAreaKey}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          style={{ minHeight: '100vh', width: '100%' }}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes location={location}>
              <Route path="/login" element={<Login />} />

              <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>}>
                <Route index element={<Inicio />} />

                {/* Clases institucionales */}
                <Route path="classes/institucional" element={<InstitutionalClasses />} />
                <Route path="classes/institucional/new" element={<InstitutionalClassForm mode="new" />} />
                <Route path="classes/institucional/:classId/edit" element={<InstitutionalClassForm mode="edit" />} />
                <Route path="classes/institucional/:classId" element={<InstitutionalClassDetail />} />

                {/* Detalle de alumno institucional y anécdotas */}
                <Route path="classes/institucional/:classId/student/:studentId" element={<StudentDetail type="institutional" />}>
                  <Route path="anecdota" element={<AnecdotarioForm type="institutional" />} />
                </Route>
                <Route path="classes/institucional/:classId/student/:studentId/attendance-history" element={<StudentAttendanceHistory type="institutional" />} />

                {/* Asistencia institucional */}
                <Route path="attendance/master-correction" element={<AttendanceMasterCorrection />} />
                <Route path="attendance/institucional/:classId" element={<AttendanceHistory type="institutional" />} />
                <Route path="attendance/institucional/:classId/new" element={<AttendanceForm type="institutional" mode="new" />} />
                <Route path="attendance/institucional/:classId/edit/:recordId" element={<AttendanceForm type="institutional" mode="edit" />} />
                <Route path="attendance/institucional/:classId/copy/:recordId" element={<AttendanceForm type="institutional" mode="copy" />} />
                <Route path="attendance/institucional/:classId/edit-pending/:createdAt" element={<AttendanceForm type="institutional" mode="edit-pending" />} />
                <Route path="attendance/institucional/:classId/report/:recordId" element={<AttendanceReport type="institutional" mode="record" />} />
                <Route path="attendance/institucional/:classId/report-pending/:createdAt" element={<AttendanceReportPending type="institutional" />} />
                <Route path="attendance/institucional/:classId/reportes" element={<AttendanceReport type="institutional" mode="range" />} />
                <Route path="classes/institucional/:classId/notas" element={<ClassNotes type="institutional" />} />

                {/* Clases personales */}
                <Route path="classes/personal" element={<PersonalClasses />} />
                <Route path="classes/personal/:classId" element={<PersonalClassDetail />} />
                <Route path="classes/personal/:classId/edit" element={<PersonalClassForm />} />
                <Route path="classes/personal/:classId/student/:studentId" element={<StudentDetail type="personal" />}>
                  <Route path="anecdota" element={<AnecdotarioForm type="personal" />} />
                </Route>
                <Route path="classes/personal/:classId/student/:studentId/attendance-history" element={<StudentAttendanceHistory type="personal" />} />

                {/* Asistencia personal */}
                <Route path="attendance/personal/:classId" element={<AttendanceHistory type="personal" />} />
                <Route path="attendance/personal/:classId/new" element={<AttendanceForm type="personal" mode="new" />} />
                <Route path="attendance/personal/:classId/edit/:recordId" element={<AttendanceForm type="personal" mode="edit" />} />
                <Route path="attendance/personal/:classId/copy/:recordId" element={<AttendanceForm type="personal" mode="copy" />} />
                <Route path="attendance/personal/:classId/edit-pending/:createdAt" element={<AttendanceForm type="personal" mode="edit-pending" />} />
                <Route path="attendance/personal/:classId/report/:recordId" element={<AttendanceReport type="personal" mode="record" />} />
                <Route path="attendance/personal/:classId/report-pending/:createdAt" element={<AttendanceReportPending type="personal" />} />
                <Route path="attendance/personal/:classId/reportes" element={<AttendanceReport type="personal" mode="range" />} />
                <Route path="classes/personal/:classId/notas" element={<ClassNotes type="personal" />} />

                {/* Institución (admin) */}
                <Route path="institution" element={<Institution />} />

                {/* Horario / Franjas */}
                <Route path="timetable" element={<Timetable />} />
                <Route path="timeslots" element={<TimeSlotAdmin />} />

                {/* Auditoría de Sistema */}
                <Route path="audit" element={<Audit />} />

                {/* Configuración (Super Admin) */}
                <Route path="settings" element={<Settings />} />

                {/* Administración de base de datos */}
                <Route path="admin-data" element={<AdminData />} />

                {/* Gestión de docentes */}
                <Route path="docentes" element={<GestionDocentes />} />

                {/* Perfil Docente */}
                <Route path="perfil" element={<PerfilDocente />} />

                {/* Reportes Institucionales */}
                <Route path="reportes-institucionales" element={<InstitutionalReports />} />

                {/* Resumen general de secciones */}
                <Route path="asistencia/resumen" element={<ResumenGeneralSecciones />} />

                {/* Buscar por NIE */}
                <Route path="estudiantes-institucionales" element={<StudentInstitutionalSearch />} />

                {/* Auditoría */}
                <Route path="audit" element={<Audit />} />

                <Route path="calendario" element={<Calendario />} />
                <Route path="calendario/admin" element={<CalendarAdminPage />} />
                <Route path="ayuda" element={<HelpModule />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </motion.div>
      </AnimatePresence>

      <Modal
        isOpen={updateModal.open}
        title="Actualización disponible"
        message="Hay una nueva versión de la aplicación con mejoras y correcciones. ¿Deseas actualizar ahora?"
        confirmText="Actualizar ahora"
        cancelText="Más tarde"
        onConfirm={handleUpdateApp}
        onCancel={() => setUpdateModal({ open: false, registration: null })}
      />
    </InstitutionProvider>
  );
}